'use client'

import { useState, useEffect } from 'react'
import { X, Shield, Calendar, MapPin, Award, User } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import BuddyTemperature from '@/components/BuddyTemperature'
import { useTheme } from '@/contexts/ThemeContext'

interface UserProfile {
  id: string
  name: string
  profile_image?: string
  certification_level?: string
  certification_agency?: string
  buddy_temperature?: number
  created_at: string
  is_student?: boolean
}

interface ProfileModalProps {
  userId: string
  isOpen: boolean
  onClose: () => void
}

export default function ProfileModal({ userId, isOpen, onClose }: ProfileModalProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (isOpen && userId) {
      loadProfile()
    }
  }, [isOpen, userId])

  const loadProfile = async () => {
    setIsLoading(true)
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, name, profile_image, certification_level, certification_agency, buddy_temperature, created_at, is_student')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('프로필 로드 오류:', error)
        return
      }

      setProfile(data)
    } catch (error) {
      console.error('프로필 로드 오류:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long'
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div className={`relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* 헤더 */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            프로필
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${
              isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin w-8 h-8 border-2 border-t-transparent rounded-full ${
                isDark ? 'border-blue-400' : 'border-blue-500'
              }`} />
            </div>
          ) : profile ? (
            <div className="space-y-6">
              {/* 프로필 사진 및 기본 정보 */}
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                  {profile.profile_image ? (
                    <img
                      src={profile.profile_image}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-white" />
                  )}
                </div>

                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {profile.name}
                </h3>

                <span className={`px-3 py-1 rounded-full text-sm ${
                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}>
                  {formatDate(profile.created_at)} 가입
                </span>

                {/* 버디 온도 */}
                <div className="mt-3">
                  <BuddyTemperature temperature={profile.buddy_temperature || 36.5} variant="inline" />
                </div>
              </div>

              {/* 다이빙 정보 */}
              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
                <h4 className={`text-sm font-semibold mb-3 flex items-center ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <Shield className="w-4 h-4 mr-2" />
                  다이빙 정보
                </h4>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      자격증 레벨
                    </span>
                    <div className="flex items-center gap-2">
                      <Award className={`w-4 h-4 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {profile.certification_level || '미설정'}
                      </span>
                    </div>
                  </div>

                  {profile.certification_agency && (
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        발급기관
                      </span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {profile.certification_agency}
                      </span>
                    </div>
                  )}

                  {profile.is_student && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 text-center">
                      <span className={`text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        학생 다이버
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              프로필을 찾을 수 없습니다
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
